import { merge, Observable, Subject } from 'rxjs';
import { map, mapTo, share } from 'rxjs/operators';
import config from 'src/site.config.json';
import {
	IScorm1_2Api,
	isScormErrorCode,
	isScormResult,
	ScormErrorCode,
	ScormParameter,
	ScormResult,
} from '../../api/1_2';
import {
	IFetchedEvent,
	ILmv6ScormApi,
	ILmv6ScormApiEvent,
	InitializingParameters,
} from '../../api/lmv6';
import { StringBuffer } from './stringBuffer';
import { TelsLmsFormatter } from './telsFormatter';

// tslint:disable-next-line:class-name
export class LmScorm1_2Api implements ILmv6ScormApi {
	/**
	 * descripotion
	 */
	public readonly description = 'SCORM 1.2 API for Generalist/LM';

	/**
	 * リクエスト完了イベント
	 */
	public event: Observable<ILmv6ScormApiEvent>;

	protected readonly element0List = [
		'cmi.objectives._children',
		'cmi.objectives._count',
		'cmi.objectives.0.id',
		'cmi.objectives.0.score._children',
		'cmi.objectives.0.score.raw',
		'cmi.objectives.0.score.min',
		'cmi.objectives.0.score.max',
		'cmi.objectives.0.status',
		'cmi.student_preference._children',
		'cmi.student_preference.audio',
		'cmi.student_preference.language',
		'cmi.student_preference.speed',
		'cmi.student_preference.text',
		'cmi.interactions._children',
		'cmi.interactions._count',
		'cmi.interactions.\\d*.id',
		'cmi.interactions.\\d*.objectives._count',
		'cmi.interactions.\\d*.objectives.0.id',
		'cmi.interactions.\\d*.time',
		'cmi.interactions.\\d*.type',
		'cmi.interactions.\\d*.correct_responses._count',
		'cmi.interactions.\\d*.correct_responses.\\d*.pattern',
		'cmi.interactions.\\d*.weighting',
		'cmi.interactions.\\d*.student_response',
		'cmi.interactions.\\d*.result',
		'cmi.interactions.\\d*.latency',
		// 下記の要素はSCORM標準要素ではない、DBに登録時に判断用フラグです。
		'cmi.interactions.\\d*.storeFlag',
		'cmi.interactions.\\d*.selfFlag',
		'cmi.interactions.\\d*.objectives.\\d*.storeFlag',
		'cmi.interactions.\\d*.correct_responses.\\d*.storeFlag',
		'cmi.interactions.\\d*.correct_responses.\\d*.selfFlag',
		'cmi.comments_from_lms',
	] as const;

	protected userId?: string;
	protected careerID?: string;
	protected itemID?: string;
	protected page?: number;
	protected scoType?: string | null;
	protected loginID?: string;
	protected lastErrorCode: ScormErrorCode = '0';
	protected isInitialized: boolean = false;
	protected interactionsTypeIndex?: number | '';
	protected isPageChanged: boolean;

	/**
	 * 内部処理用
	 * 一回でもinitialize()されるとtrue
	 */
	protected configured: boolean = false;

	/**
	 * invokeされるべき
	 */
	protected readonly invokeQueue: IInvokeParams[] = [];

	/**
	 * ログ格納用: 開発者用コンソールから確認する想定
	 */
	protected readonly logs: Array<{
		at: Date;
		method: string;
		params: object;
	}> = [];

	/**
	 * fetchedイベントEmit用
	 */
	protected readonly fetchedEventSubject: Subject<
		Omit<IFetchedEvent, 'type'>
	> = new Subject();

	/**
	 * flushedイベントEmit用
	 */
	protected readonly flushedEventSubject: Subject<void> = new Subject();

	/**
	 * ctor
	 */
	public constructor() {
		// 外に出すイベントを準備: 各Subjectから集めつつtypeを付加して送出
		this.event = merge(
			this.fetchedEventSubject.pipe(
				map(e => ({ type: 'fetched' as const, ...e }))
			),
			this.flushedEventSubject.pipe(mapTo({ type: 'flushed' as const }))
		).pipe(share());
	}

	/**
	 * 初期化
	 * @param param0
	 */
	public initialize(params: InitializingParameters) {
		// 先に前回の終了処理
		this.flush();

		({
			careerID: this.careerID,
			itemID: this.itemID,
			loginID: this.loginID,
			page: this.page,
			scoType: this.scoType,
			userId: this.userId,
		} = params);

		this.configured = true;

		this.pushLog('initialize');
	}

	/**
	 * 未使用時用初期化
	 */
	public flush() {
		try {
			return this.configured ? this.invokeRest() : false;
		} finally {
			this.resetLog();
			this.configured = false;

			this.lastErrorCode = '0';
			this.isInitialized = false;
			this.interactionsTypeIndex = '';

			this.userId = void 0;
			this.careerID = void 0;
			this.itemID = void 0;
			this.page = void 0;
			this.scoType = void 0;
			this.loginID = void 0;

			// イベント
			this.flushedEventSubject.next();
		}
	}

	/**
	 * エラー内容を取得する。
	 * @param errorCode エラーコード
	 * @return エラー内容
	 */
	public LMSGetErrorString(errorCode: ScormErrorCode) {
		this.pushLog('LMSGetErrorString', { errorCode });
		if (errorCode == null || errorCode.length === 0) {
			return '';
		}
		return this.invoke({
			command: 'err',
			defaultValue: ' LMS not found',
			element: null,
			method: 'LMSGetErrorString',
			value: errorCode,
		});
	}

	/**
	 * 最後のエラーを取得する。
	 * @return エラーコード
	 */
	public LMSGetLastError() {
		this.pushLog('LMSGetLastError');
		return this.lastErrorCode;
	}

	/**
	 * 送信したメッセージをコミットする。
	 * @param parameter 予約されている
	 * @return 結果
	 */
	public LMSCommit(parameter: ''): ScormResult {
		this.pushLog('LMSCommit', { parameter });
		if (!this.isInitialized || this.isPageChanged) {
			this.lastErrorCode = '301';
			return 'false';
		}
		if (parameter == null || parameter.length > 0) {
			this.lastErrorCode = '201';
			return 'false';
		}

		const params: IInvokeParams = {
			command: 'commit',

			continuation: result => {
				if (isScormResult(result)) {
					this.pushLog('LMSCommit: completed', {
						parameter,
						result,
					});
					return result;
				} else {
					this.pushLog('LMSCommit: unknown result', {
						parameter,
						result,
					});
					this.lastErrorCode = '101';
					return 'false';
				}
			},

			defaultValue: 'false',
			element: parameter,
			method: 'LMSCommit',
			value: null,
		};

		// #8289: invokeを遅延 -> 同期的にはtrueしか返せない
		this.holdInvocation(params);

		return 'true';
	}

	/**
	 * エレメントに対応する値を取得する。
	 * @param element エレメント
	 * @return 結果
	 */
	public LMSGetValue(element: ScormParameter) {
		this.pushLog('LMSGetValue', { element });
		if (!this.isInitialized || this.isPageChanged) {
			this.lastErrorCode = '301';
			return '';
		}
		if (element == null || element.length === 0) {
			this.lastErrorCode = '201';
			return '';
		}
		const kekka = this.ElementPreCheck(false, element, null);
		if (!('0' === kekka)) {
			this.lastErrorCode = kekka;
			return '';
		}
		return this.invoke({
			command: 'get',
			defaultValue: '',
			element,
			method: 'LMSGetValue',
			value: null,
		});
	}

	/**
	 * 初期化し、セッションを開始する。
	 * @param parameter 予約されている
	 * @return 結果
	 */
	public LMSInitialize(parameter: ''): ScormResult {
		this.pushLog('LMSInitialize', { parameter });
		if (this.isInitialized) {
			this.lastErrorCode = '101';
			return 'false';
		}
		if (parameter == null || parameter.length > 0) {
			this.lastErrorCode = '201';
			return 'false';
		}
		const rtn = this.invoke({
			command: 'init',
			defaultValue: 'false',
			element: parameter,
			method: 'LMSInitialize',
			value:
				this.userId +
				',' +
				this.loginID +
				',' +
				this.careerID +
				',' +
				this.itemID,
		});
		this.pushLog('LMSInitialize: result', { parameter, rtn });
		if (!isScormResult(rtn)) {
			this.lastErrorCode = '101';
			return 'false';
		}
		if (rtn.toLowerCase() === 'true') {
			this.isInitialized = true;
		} else {
			this.isInitialized = false;
		}
		this.isPageChanged = false;

		return rtn;
	}

	/**
	 * エレメントに対応する値をセットする。
	 * @param element エレメント
	 * @param value 値
	 * @return 結果
	 */
	public LMSSetValue(element: ScormParameter, value: string): ScormResult {
		this.pushLog('LMSSetValue', { element, value });
		if (!this.isInitialized || this.isPageChanged) {
			this.lastErrorCode = '301';
			this.pushLog('LMSSetValue: error', {
				element,
				isInitialized: this.isInitialized,
				isPageChanged: this.isPageChanged,
				lastErrorCode: this.lastErrorCode,
				value,
			});
			return 'false';
		}
		if (element == null || element.length === 0 || value == null) {
			this.lastErrorCode = '201';
			this.pushLog('LMSSetValue: error', {
				element,
				isInitialized: this.isInitialized,
				isPageChanged: this.isPageChanged,
				lastErrorCode: this.lastErrorCode,
				value,
			});
			return 'false';
		}
		value = value.toString();
		const kekka = this.ElementPreCheck(true, element, value);
		if ('0' !== kekka) {
			this.lastErrorCode = kekka;
			return 'false';
		}

		const params: IInvokeParams = {
			command: 'set',

			continuation: result => {
				if (isScormResult(result)) {
					this.pushLog('LMSSetValue: completed', {
						element,
						result,
						value,
					});
					return result;
				} else {
					this.pushLog('LMSSetValue: unknown result', {
						element,
						result,
						value,
					});
					this.lastErrorCode = '101';
					return 'false';
				}
			},

			defaultValue: 'false',
			element,
			method: 'LMSSetValue',
			value: this.verifyData(value),
		};

		// #8289: invokeを遅延 -> 同期的にはtrueしか返せない
		this.holdInvocation(params);

		return 'true';
	}

	/**
	 * セッションを閉じる。
	 * @param parameter 予約されている
	 * @return 結果
	 */
	public LMSFinish(parameter: ''): ScormResult {
		this.pushLog('LMSFinish', { parameter });
		if (!this.isInitialized) {
			this.lastErrorCode = '301';
			return 'false';
		}
		if (parameter == null || parameter.length > 0) {
			this.lastErrorCode = '201';
			return 'false';
		}

		const params: IInvokeParams = {
			command: 'finish',

			continuation: result => {
				if (!isScormResult(result)) {
					this.pushLog('LMSFinish: unknown result', {
						parameter,
						result,
					});
					this.lastErrorCode = '101';
					return 'false';
				}
				if (result.toLowerCase() === 'true') {
					this.isInitialized = false;
				}
				this.pushLog('LMSFinish: completed', {
					parameter,
					result,
				});
				return result;
			},

			defaultValue: 'false',
			element: parameter,
			method: 'LMSFinish',
			value: null,
		};

		// #8289: invokeを遅延 -> 同期的にはtrueしか返せない
		this.holdInvocation(params);

		return 'true';
	}

	/**
	 * エラーの補足を取得する。
	 * @param errorCode エラーコード
	 * @return 補足
	 */
	public LMSGetDiagnostic(errorCode: ScormErrorCode) {
		this.pushLog('LMSGetDiagnostic', { errorCode });
		if (errorCode == null || errorCode.length === 0) {
			errorCode = this.lastErrorCode;
		}
		return this.invoke({
			command: 'diag',
			defaultValue: 'LMSが見つかりません。',
			element: null,
			method: 'LMSGetDiagnostic',
			value: errorCode,
		});
	}

	/**
	 * ログ追加
	 * @param method 実行中のメソッド名
	 * @param params 追加情報
	 */
	protected pushLog(method: string, params: object = {}) {
		this.logs.push({ at: new Date(), method, params });
		return this.logs;
	}

	/**
	 * ログ消去
	 */
	protected resetLog() {
		this.logs.splice(0);
	}

	/**
	 * SCOで実行されたメソッドの実装
	 * @param command コマンド
	 * @param element エレメント
	 * @param value 値
	 * @param defaultValue
	 * @param method 実行中のSCORM API
	 * @return 結果
	 */
	protected invokeImpl({
		command,
		defaultValue,
		element,
		method,
		value,
		continuation,
	}: IInvokeParams) {
		this.pushLog('invoke', {
			command,
			defaultValue,
			element,
			value,
		});
		if (
			!(command.substring(0, 3) === 'err') &&
			!(command.substring(0, 3) === 'dia')
		) {
			this.lastErrorCode = '0';
		}

		// メッセージ送信
		const format = new TelsLmsFormatter();
		const result = this.sendMessage(
			format.begin() +
				format.format(command, element, value) +
				format.end(),
			defaultValue,
			method
		);

		if (continuation) {
			const res = continuation(result);
			return res === void 0 ? 'true' : res;
		} else {
			return result;
		}
	}

	/**
	 * 置換すべき文字を置換する。
	 */
	protected verifyData(data: string | null | undefined) {
		this.pushLog('verifyData', { data });
		if (data == null) {
			return '';
		}
		// const changed = false;
		const rtn = new StringBuffer();
		for (let i = 0; i < data.length; i++) {
			const c = data.charAt(i);
			if (c === '<') {
				rtn.append('&lt;');
			} else if (c === '>') {
				rtn.append('&gt;');
			} else if (c === '&') {
				rtn.append('&amp;');
			} else if (c === '"') {
				rtn.append('&quot;');
			} else {
				rtn.append(c);
			}
		}
		return rtn.toString();
	}

	protected TelsGetPageType() {
		return this.scoType;
	}

	protected TelsGetItemID() {
		return this.itemID;
	}

	protected TelsGetPage() {
		return this.page;
	}

	// Send Request Function
	protected sendMessage(
		message: Parameters<typeof encodeURIComponent>[0],
		defaultvalue: string,
		method: keyof IScorm1_2Api
	): string {
		this.pushLog('sendMessage', { message, defaultvalue });

		// 同期リクエスト必須
		const xhr = new XMLHttpRequest();
		xhr.addEventListener('load', ev => {
			this.pushLog('xhr.onload', { ev, xhr });
		});
		xhr.open(
			'POST',
			`${config.tsqPath}/tsq/pkg/servlet/LmcmLMSServlet`,
			false
		);
		xhr.setRequestHeader(
			'Content-Type',
			'application/x-www-form-urlencoded'
		);

		// synchronously
		xhr.send(`clientxml=${escape(encodeURIComponent(message))}`);

		this.pushLog('sendMessage: sent', {
			defaultvalue,
			message,
			xhr,
		});

		this.fetchedEventSubject.next({
			body: xhr.responseText,
			method,
		});
		const result = this.processResponse(xhr.responseText);
		return !!result ? result : defaultvalue;
	}

	protected processResponse(response: string) {
		this.pushLog('processResponse', { response });
		const result = response.split('\r\n');
		let ecode: ScormErrorCode | undefined;
		let res;
		let itemIDTemp = this.itemID;
		let scoTypeTemp = this.scoType;
		let pageTemp = this.page;
		let gol;

		for (const r of result) {
			if (r.indexOf('error-code') !== -1) {
				const ecodeString = r.substring(r.indexOf('=') + 1);
				if (isScormErrorCode(ecodeString)) {
					ecode = ecodeString;
				} else {
					throw new Error(
						`ecode_: ${ecodeString} is not a SCORM Error Code`
					);
				}
			}
			if (r.indexOf('result') !== -1) {
				res = r.substring(r.indexOf('=') + 1);
				res = res.replace(/\\:/g, ':');
				res = res.replace(/\\=/g, '=');
				res = res.replace(/\\#/g, '#');
				res = res.replace(/\\!/g, '!');
				res = res.replace(/\\\\/g, '\\');
			}

			if (r.indexOf('itemID') !== -1) {
				itemIDTemp = r.substring(r.indexOf('=') + 1);
			}
			if (r.indexOf('scoType') !== -1) {
				scoTypeTemp = r.substring(r.indexOf('=') + 1);
			}
			if (r.indexOf('page') !== -1) {
				pageTemp = parseInt(r.substring(r.indexOf('=') + 1), 10);
			}
		}

		try {
			this.itemID = itemIDTemp;
			this.scoType = scoTypeTemp;
			this.page = pageTemp;
			if (this.itemID == null || this.scoType == null) {
				throw new Error('LMS内部例外が発生しました。');
			}

			if (ecode == null || res == null) {
				throw new Error('LMS内部例外が発生しました。');
			}
			this.lastErrorCode = ecode;
			gol = res;
			return res;
		} catch (e) {
			// tslint:disable-next-line:no-console
			console.error(e, this.itemID, this.scoType, this.page);
			this.lastErrorCode = '101';
			return gol;
		}
	}

	/**
	 * 非実装エレメントの処理および添え字のあるエレメントの事前チェック
	 * これ以外のチェックはサーバに任せる。
	 * @param setflg false:LMSGetValue()のチェック、true:LMSSetValue()のチェック
	 * @param element エレメント
	 * @param value LMSSetValue()でSetする値
	 * @return 結果 "0":事前チェックは正常、あるいは事前チェック対象外
	 * "0"以外:エラー
	 */
	protected ElementPreCheck(
		setflg: boolean,
		element: ScormParameter,
		value: string | null
	) {
		this.pushLog('ElementPreCheck', { setflg, element, value });
		if (element == null || element.length === 0) {
			return '201'; // 引数エラー
		}

		if (setflg && value == null) {
			return '201'; // 引数エラー
		}

		if (
			!(
				(element.length >= 15 &&
					'cmi.objectives.' === element.substring(0, 15)) ||
				(element.length >= 23 &&
					'cmi.student_preference.' === element.substring(0, 23)) ||
				(element.length >= 17 &&
					'cmi.interactions.' === element.substring(0, 17)) ||
				'cmi.comments_from_lms' === element
			)
		) {
			return '0'; // チェック対象外　サーバへ
		}

		// 添え字を「0」に変えた文字列の生成
		const elementAry = element.split('\\.');
		let element0 = '';
		for (let i = 0; i < elementAry.length; i++) {
			if (i > 0) {
				element0 = element0 + '.';
			}
			if (this.isCMIInteger(elementAry[i])) {
				element0 = element0 + '0';
			} else {
				element0 = element0 + elementAry[i];
			}
		}

		let findnum = -1;
		for (let i = 0; i < this.element0List.length; i++) {
			if (element0 === this.element0List[i]) {
				findnum = i;
				break;
			}
			if (element0.substring(0, 17) === 'cmi.interactions.') {
				const regex = new RegExp(this.element0List[i]);
				if (regex.test(element0)) {
					findnum = i;
					break;
				}
			}
			if (element0 === this.element0List[i] + '._children' && !setflg) {
				return '202'; // このエレメントは"_children"をサポートしていない
			}
			if (element0 === this.element0List[i] + '._conut' && !setflg) {
				return '203'; // このエレメントは"_count"をサポートしていない
			}
		}

		switch (findnum) {
			case -1:
				return '201'; // 無効な引数エラー
			case 0: // "cmi.objectives._children"
				if (setflg) {
					return '402'; // キーワード"_children"に書き込みを行おうとした
				}
				break;
			case 1: // "cmi.objectives._count"
				if (setflg) {
					return '402'; // キーワード"_count"に書き込みを行おうとした
				}
				break;
			case 2: // "cmi.objectives.0.id"
				if (setflg && !this.isCMIString255(value)) {
					return '405'; // 不正データタイプ(valueがASCIIでない)
				}
				break;
			case 3: // "cmi.objectives.0.score._children"
				if (setflg) {
					return '402'; // キーワード"_children"に書き込みを行おうとした
				}
				break;
			case 4: // "cmi.objectives.0.score.raw"
				if (
					setflg &&
					!(
						this.isCMIBlank(value) ||
						(this.isCMIDecimal(value) &&
							0 <= Number(value) &&
							Number(value) <= 100)
					)
				) {
					return '405'; // 不正データタイプ(valueが0～100でない)
				}
				break;
			case 5: // "cmi.objectives.0.score.min"
				if (
					setflg &&
					!(
						this.isCMIBlank(value) ||
						(this.isCMIDecimal(value) &&
							0 <= Number(value) &&
							Number(value) <= 100)
					)
				) {
					return '405'; // 不正データタイプ(valueが0～100でない)
				}
				break;
			case 6: // "cmi.objectives.0.score.max"
				if (
					setflg &&
					!(
						this.isCMIBlank(value) ||
						(this.isCMIDecimal(value) &&
							0 <= Number(value) &&
							Number(value) <= 100)
					)
				) {
					return '405'; // 不正データタイプ(valueが0～100でない)
				}
				break;
			case 7: // "cmi.objectives.0.status"
				if (
					setflg &&
					!(
						value &&
						[
							'passed',
							'completed',
							'failed',
							'incomplete',
							'browsed',
							'not attempted',
						].includes(value)
					)
				) {
					return '405'; // 不正データタイプ(valueが決められた文字列以外)
				}
				break;
			case 13: // "cmi.interactions._children"
				if (setflg) {
					return '402'; // キーワード"_children"に書き込みを行おうとした
				}
				break;
			case 14: // "cmi.interactions._count"
				if (setflg) {
					return '402'; // キーワード"_count"に書き込みを行おうとした
				}
				break;
			case 15: // "cmi.interactions.n.id"
				if (setflg && !this.isCMIIdentifier(value)) {
					this.sendStoreFlag(element);
					return '405'; // 不正データタイプ
				}
				break;
			case 16: // "cmi.interactions.n.objectives._count"
				if (setflg) {
					return '402'; // キーワード"cmi.interactions.n.objectives._count" に書き込みを行おうとした
				}
				break;
			case 17: // "cmi.interactions.n.objectives.0.id"
				if (setflg && !this.isCMIIdentifier(value)) {
					this.sendStoreFlag(element);
					return '405'; // 不正データタイプ
				}
				break;
			case 18: // "cmi.interactions.n.time"
				if (setflg && !this.isCMITime(value)) {
					this.sendStoreFlag(element);
					return '405'; // 不正データタイプ
				}
				break;
			case 19: // "cmi.interactions.n.type"
				if (setflg && !this.isCMIVocabulary(value)) {
					this.sendStoreFlag(element);
					return '405'; // 不正データタイプ
				}
				break;
			case 20: // "cmi.interactions.n.correct_responses._count"
				if (setflg) {
					return '402'; // キーワード"cmi.interactions.n.correct_responses._count" に書き込みを行おうとした
				}
				break;
			case 21: // "cmi.interactions.n.correct_responses.m.pattern"
				if (setflg && !this.isCMIFeedback(value)) {
					return '405'; // 不正データタイプ
				} else {
					this.sendSelfFlag(element);
				}
				break;
			case 22: // "cmi.interactions.n.weighting"
				if (setflg && !this.isCMIDecimal(value)) {
					this.sendStoreFlag(element);
					return '405'; // 不正データタイプ
				}
				break;
			case 23: // "cmi.interactions.n.student_response"
				if (setflg && !this.isCMIFeedback(value)) {
					return '405'; // 不正データタイプ
				} else {
					this.sendSelfFlag(element);
				}
				break;
			case 24: // "cmi.interactions.n.result"
				if (
					setflg &&
					!(
						this.isCMIDecimal(value) ||
						(value &&
							[
								'correct',
								'wrong',
								'unanticipated',
								'neutral',
							].includes(value))
					)
				) {
					this.sendStoreFlag(element);
					return '405'; // 不正データタイプ
				}
				break;
			case 25: // "cmi.interactions.n.latency"
				if (setflg && !this.isCMITimespan(value)) {
					this.sendStoreFlag(element);
					return '405'; // 不正データタイプ
				}
				break;
			case 26: // "cmi.interactions.n.storeFlag"
			case 27: // "cmi.interactions.n.selfFlag"
			case 28: // "cmi.interactions.n.objectives.m.storeFlag"
			case 29: // "cmi.interactions.n.correct_responses.m.storeFlag"
			case 30: // "cmi.interactions.n.correct_responses.m.selfFlag"
				break;
			default:
				// "cmi.student_preference.*"、"cmi.interactions.*"、"cmi.comments_from_lms"
				return '401'; // 非実装
		}

		return '0'; // 事前チェックは正常
	}

	protected isCMIBlank(data: string | null | undefined): data is '' {
		return data != null && data.length === 0;
	}

	protected isCMIDecimal(data: string | null | undefined) {
		let rtn = false;
		const i = Number(data);
		if (isNaN(i)) {
			return rtn;
		} else {
			rtn = true;
			return rtn;
		}
	}

	protected isCMIInteger(data: string | null | undefined) {
		if (!data) {
			return false;
		}
		let rtn = false;
		const i = Number(data);
		if (isNaN(i)) {
			return false;
		} else if (parseInt(data, 10) === parseFloat(data)) {
			rtn = 0 <= parseInt(data, 10) && parseInt(data, 10) <= 65536;
			return rtn;
		} else {
			return false;
		}
	}

	protected isCMIString255(data: string | null | undefined) {
		if (!data) {
			return false;
		}
		const chars = new Array(data.length);
		for (let i = 0; i < data.length - 1; i++) {
			chars[i] = data.charCodeAt(i);
		}
		if (chars.length > 255) {
			return false;
		}

		for (const c of chars) {
			if (c < 0 || c > 0x7f) {
				return false;
			}
		}
		return true;
	}

	protected isCMIIdentifier(data: string | null | undefined) {
		if (!data) {
			return false;
		}
		return data.length < 256 && /^[\w_-]+$/.test(data);
	}

	protected isCMITime(data: string | null | undefined) {
		if (!data) {
			return false;
		}
		const reg = /^(2[0-3]|[0-1][0-9]):[0-5][0-9]:[0-5][0-9](\.\d{1,2})?$/;
		return reg.test(data);
	}

	protected isCMIVocabulary(data: string | null | undefined) {
		if (!data) {
			return false;
		}
		let flag = false;
		const interactionData = new Array(
			'true-false',
			'choice',
			'fill-in',
			'matching',
			'performance',
			'likert',
			'sequencing',
			'numeric'
		);
		for (let i = 0; i < interactionData.length; i++) {
			if (data === interactionData[i]) {
				flag = true;
				this.interactionsTypeIndex = i;
			}
		}
		return flag;
	}

	protected isCMIFeedback(data: string | null | undefined) {
		if (!data) {
			return false;
		}
		let flag = false;
		const typeIndex = this.interactionsTypeIndex;
		data = data.toLowerCase();

		switch (typeIndex) {
			case 0: // true-false
				flag = /^[01tf].*$/.test(data);
				break;
			case 1: // choice
				flag =
					data.length <= 26 &&
					!/[^0-9a-z\[\],{}]/.test(data) &&
					(/^\{?(,?[0-9a-z]+\b)+\}?$/.test(data) ||
						/^\{?((\[,\])?[0-9a-z]+\b)+\}?$/.test(data));

				break;
			case 2: // fill-in
				flag = data.length < 256;
				break;
			case 3: // matching
				flag = /^\{?(,?.\..)+\}?$/.test(data);
				break;
			case 4: // performance
				flag =
					data.length === 0 ||
					(data.length < 256 && /^[0-9a-z]+$/.test(data));
				break;
			case 5: // likert
				flag = true;
				break;
			case 6: // sequencing
				flag = /^(,?[0-9a-z])+$/.test(data);
				break;
			case 7: // numeric
				flag = this.isCMIDecimal(data);
				break;
			default:
				flag = false;
		}
		return flag;
	}

	protected isCMITimespan(data: string | null | undefined) {
		if (!data) {
			return false;
		}
		const reg = /^(\d{2,4}:[0-9][0-9]:)?[0-9][0-9](\.\d{1,2})?$/;
		return reg.test(data);
	}

	protected sendStoreFlag(element: ScormParameter) {
		this.pushLog('sendStoreFlag', { element });
		// DBに登録フラグを設定する。
		const pointIndexLast = element.lastIndexOf('.');
		const elementStr = element.substring(0, pointIndexLast + 1);
		const elementFlag = elementStr + 'storeFlag';
		const elementFlagValue = '0';
		this.LMSSetValue(elementFlag, elementFlagValue);
	}

	protected sendSelfFlag(element: ScormParameter) {
		this.pushLog('sendSelfFlag', { element });
		// DBに登録フラグを設定する。
		const pointIndexLast = element.lastIndexOf('.');
		const elementStr = element.substring(0, pointIndexLast + 1);
		const elementFlag = elementStr + 'selfFlag';
		const elementFlagValue = '1';
		this.LMSSetValue(elementFlag, elementFlagValue);
	}

	/**
	 * Queueに残っているリクエストをすべて送出してQueueをリセットする
	 */
	protected invokeRest() {
		try {
			return this.invokeQueue.length > 0;
		} finally {
			for (const param of this.invokeQueue) {
				this.invokeImpl(param);
			}
			this.invokeQueue.splice(0);
		}
	}

	/**
	 * 送出を遅延させるinvokeの引数をQueueに追加
	 * @param params 引数
	 */
	protected holdInvocation(params: IInvokeParams) {
		this.invokeQueue.push(params);
	}

	/**
	 * サーバ上のSCORM API起動: Queueが残っていれば事前に実行
	 * @param params 引数
	 */
	protected invoke(params: IInvokeParams) {
		if (this.invokeQueue.length > 0) {
			this.invokeRest();
		}
		return this.invokeImpl(params);
	}
}

/**
 * invoke()用引数
 */
interface IInvokeParams {
	/** コマンド */
	command: string;
	/** エレメント */
	element: string | null | undefined;
	/** 値 */
	value: string | null | undefined;
	/** 省略時の値 */
	defaultValue: string;
	/** メソッド種別(ログ用) */
	method: keyof IScorm1_2Api;

	/** 継続 */
	continuation?: (result: string) => ScormResult | void;
}



// WEBPACK FOOTER //
// ./src/logic/scorm/impl/legacy/LmScorm1_2Api.ts